import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PortalDashboardGroupService } from '../../services/portalDashboardGroup.service';
import { PagerDataObject } from '../../entities/pagerDataObject';
import { SharedService } from '../../services/sharedService';

@Component({
    selector: 'app-portal-dashboard-group',
    templateUrl: './portal-dashboard-group.component.html',
    standalone: false
})
export class PortalDashboardGroupComponent implements OnInit, AfterViewInit, OnDestroy {

  // Same 'view' button-column convention as dashboard.service.ts / service.service.ts:
  // metaInfo omits it, so ntr-datatable's +1 offset lines the rest up.
  columns: Array<any> = [
    { key: 'view', style: { 'flex': '0 0 100px', 'width': '1%' }, button: true },
    { name: 'Sort order', tag: 'PORTAL.GROUP_SORT_ORDER', key: 'sortOrder' },
    { name: 'Name', tag: 'PORTAL.GROUP_LABEL', key: 'label' },
    { name: 'Key', tag: 'PORTAL.GROUP_KEY', key: 'key' },
    { name: 'Description', tag: 'PORTAL.GROUP_DESCRIPTION', key: 'description' },
  ];
  metaInfo: string[] = ['sortOrder', 'label', 'key', 'description'];
  data: PagerDataObject;
  subscription: any;
  private dataSub$ = new Subject<any>();
  query: any;

  constructor(private groupService: PortalDashboardGroupService,
              private alertService: ToastrService,
              private translate: TranslateService,
              private sharedService: SharedService) {
    // ntr-datatable fires its own onQuery on init (and on every page/sort
    // change) regardless of whether this list actually supports server-side
    // filtering. Without a subscriber to answer it with a fresh [data], its
    // `loading` flag flips true and never flips back -- the spinner overlay
    // just spins forever. This list has no filter/sort params to send the
    // server, so the query is ignored and the same full list is re-fetched,
    // matching the dataSub$/switchMap convention every other admin list uses.
    this.subscription = this.dataSub$.pipe(switchMap(() => this.groupService.list()))
      .subscribe(groups => {
        const records = groups || [];
        this.data = {
          records,
          entity: 'portalDashboardGroup',
          total: records.length,
          from: records.length ? 1 : 0,
          to: records.length
        };
      });
  }

  ngOnInit(): void {
    this.load();
  }

  // Every admin screen populates the side menu itself; without this the aside
  // renders empty. Deliberately in ngAfterViewInit, matching its siblings —
  // the emitter's subscriber lives in a parent that is not ready during init.
  ngAfterViewInit(): void {
    this.sharedService.loadMenuItems('admin');
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onGridQuery(query: any): void {
    this.query = query;
    this.dataSub$.next(query);
  }

  load(): void {
    this.dataSub$.next(this.query);
  }

  // The server refuses (409) while dashboards still reference the group, and
  // says which ones -- that message is the whole value of the refusal, so it
  // is worth surfacing even though the generic delete flow below doesn't
  // otherwise report errors.
  deleteGroup(id: any): void {
    this.groupService.delete(id).subscribe(
      () => { this.load(); },
      err => {
        this.alertService.error(err?.error?.message || this.translate.instant('PORTAL.GROUP_DELETE_FAILED'));
      }
    );
  }
}
